/**
 * useDemandPlanningData Hook
 *
 * Fetches aggregated demand forecast data and supply forecast from the database,
 * matches supply to demand, and groups by week for display in the DemandPlanningTimeline.
 *
 * @module useDemandPlanningData
 */

import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../../supabase-client'
import { toast } from 'sonner'

/**
 * @typedef {'fulfilled' | 'partial' | 'shortfall' | 'unknown'} SupplyStatus
 */

/**
 * @typedef {Object} SupplyInfo
 * @property {number} expectedKg - Expected supply in kg
 * @property {number} batchCount - Number of batches
 * @property {string} confidence - Confidence level (high/medium/low)
 * @property {SupplyStatus} status - Supply status relative to demand
 * @property {number} coveragePercent - Percentage of demand covered
 * @property {Date|null} startBatchBy - Date to start batch to meet demand (shortfall only)
 * @property {number|null} leadTimeDays - Total lead time in days for this strain
 */

/**
 * @typedef {Object} DemandItem
 * @property {string} strainName - Product strain name
 * @property {number} recurringKg - Confirmed recurring order demand
 * @property {number} predictedKg - AI-predicted demand
 * @property {number} predictedConfidence - Confidence score (0-1)
 * @property {number} totalKg - Sum of recurring + predicted
 * @property {number} adjustedKg - Weighted by confidence
 * @property {SupplyInfo|null} supply - Supply information if available
 */

/**
 * @typedef {Object} WeeklyDemand
 * @property {string} weekStart - ISO date string for week start
 * @property {Date} weekStartDate - Date object for week start
 * @property {string} weekLabel - Human-readable week label
 * @property {DemandItem[]} items - Demand items for this week
 * @property {number} totalKg - Total demand for the week
 */

/**
 * Format a date as a week label (e.g., "27 Jan - 2 Feb")
 *
 * @param {Date} weekStart - Start of week
 * @returns {string} Formatted week label
 */
function formatWeekLabel(weekStart) {
  const weekEnd = new Date(weekStart)
  weekEnd.setDate(weekEnd.getDate() + 6)

  const startDay = weekStart.getDate()
  const startMonth = weekStart.toLocaleDateString('en-GB', { month: 'short' })
  const endDay = weekEnd.getDate()
  const endMonth = weekEnd.toLocaleDateString('en-GB', { month: 'short' })

  // Same month: "27 - 31 Jan"
  if (startMonth === endMonth) {
    return `${startDay} - ${endDay} ${startMonth}`
  }

  // Different months: "27 Jan - 2 Feb"
  return `${startDay} ${startMonth} - ${endDay} ${endMonth}`
}

/**
 * Calculate supply status based on coverage percentage
 *
 * @param {number} supplyKg - Expected supply
 * @param {number} demandKg - Required demand
 * @returns {{ status: SupplyStatus, coveragePercent: number }}
 */
function calculateSupplyStatus(supplyKg, demandKg) {
  if (demandKg <= 0) {
    return { status: 'fulfilled', coveragePercent: 100 }
  }

  const coveragePercent = Math.min(100, Math.round((supplyKg / demandKg) * 100))

  if (coveragePercent >= 100) {
    return { status: 'fulfilled', coveragePercent }
  } else if (coveragePercent >= 50) {
    return { status: 'partial', coveragePercent }
  } else {
    return { status: 'shortfall', coveragePercent }
  }
}

/**
 * Custom hook for demand planning data with supply matching
 *
 * @param {Object} options
 * @param {number} [options.weeksAhead=12] - Number of weeks to fetch
 * @returns {Object} Demand data and control functions
 */
export function useDemandPlanningData({ weeksAhead = 12 } = {}) {
  const [weeklyDemand, setWeeklyDemand] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  /**
   * Fetch and transform demand + supply data
   */
  const fetchData = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      // Fetch demand and supply forecasts
      const [demandResult, supplyResult] = await Promise.all([
        supabase.rpc('get_aggregated_demand', { weeks_ahead: weeksAhead }),
        supabase.rpc('get_supply_forecast', { p_weeks_ahead: weeksAhead })
      ])

      if (demandResult.error) throw demandResult.error

      // Fetch strain-specific effective timing (calculated if available, else typical)
      const strainNames = [...new Set((demandResult.data || []).map(r => r.strain_name))]
      const strainTimingResults = await Promise.all(
        strainNames.map(name =>
          supabase.rpc('get_effective_strain_timing_by_name', { p_strain_name: name })
        )
      )

      // Build strain lead time lookup: strain_name -> total lead days
      const strainLeadTimes = new Map()
      strainNames.forEach((name, index) => {
        const result = strainTimingResults[index]
        if (!result.error && result.data && result.data.length > 0) {
          const timing = result.data[0]
          strainLeadTimes.set(name, timing.total_lead_days)
        } else {
          // Fallback to default if function fails
          strainLeadTimes.set(name, 51) // ~14+14+16+7
        }
      })

      // Build supply lookup map: week_start + strain_name -> supply info
      const supplyMap = new Map()
      if (!supplyResult.error && supplyResult.data) {
        for (const row of supplyResult.data) {
          const key = `${row.week_start}|${row.strain_name}`
          supplyMap.set(key, {
            expectedKg: Number(row.expected_kg) || 0,
            batchCount: Number(row.batch_count) || 0,
            confidence: row.avg_confidence || 'unknown'
          })
        }
      }

      // Group demand by week and match with supply
      const weekMap = new Map()

      for (const row of demandResult.data || []) {
        const weekKey = row.week_start

        if (!weekMap.has(weekKey)) {
          const weekStartDate = new Date(row.week_start)
          weekMap.set(weekKey, {
            weekStart: weekKey,
            weekStartDate,
            weekLabel: formatWeekLabel(weekStartDate),
            items: [],
            totalKg: 0
          })
        }

        // Look up supply for this week + strain
        const supplyKey = `${row.week_start}|${row.strain_name}`
        const supplyInfo = supplyMap.get(supplyKey)
        const leadTimeDays = strainLeadTimes.get(row.strain_name) || 51 // Default ~51 days

        const totalKg = Number(row.total_kg) || 0
        let supply = null

        if (supplyInfo) {
          const { status, coveragePercent } = calculateSupplyStatus(supplyInfo.expectedKg, totalKg)

          // Calculate start batch by date for non-fulfilled items
          let startBatchBy = null
          if (status !== 'fulfilled') {
            const weekStart = new Date(row.week_start)
            startBatchBy = new Date(weekStart)
            startBatchBy.setDate(startBatchBy.getDate() - leadTimeDays)
          }

          supply = {
            ...supplyInfo,
            status,
            coveragePercent,
            startBatchBy,
            leadTimeDays
          }
        } else {
          // No supply found for this strain/week - calculate start batch by date
          const weekStart = new Date(row.week_start)
          const startBatchBy = new Date(weekStart)
          startBatchBy.setDate(startBatchBy.getDate() - leadTimeDays)

          supply = {
            expectedKg: 0,
            batchCount: 0,
            confidence: 'unknown',
            status: totalKg > 0 ? 'shortfall' : 'fulfilled',
            coveragePercent: totalKg > 0 ? 0 : 100,
            startBatchBy: totalKg > 0 ? startBatchBy : null,
            leadTimeDays
          }
        }

        const week = weekMap.get(weekKey)
        week.items.push({
          strainName: row.strain_name,
          recurringKg: Number(row.recurring_kg) || 0,
          predictedKg: Number(row.predicted_kg) || 0,
          predictedConfidence: Number(row.predicted_confidence) || 0,
          totalKg,
          adjustedKg: Number(row.adjusted_kg) || 0,
          supply
        })
        week.totalKg += totalKg
      }

      // Convert to sorted array
      const weeks = Array.from(weekMap.values())
        .sort((a, b) => new Date(a.weekStart) - new Date(b.weekStart))

      setWeeklyDemand(weeks)
    } catch (err) {
      console.error('[useDemandPlanningData] Error fetching data:', err)
      setError(err)
      toast.error('Failed to load demand forecast', {
        description: err.message
      })
    } finally {
      setLoading(false)
    }
  }, [weeksAhead])

  /**
   * Refresh data
   */
  const refresh = useCallback(() => {
    fetchData()
  }, [fetchData])

  // Initial fetch
  useEffect(() => {
    fetchData()
  }, [fetchData])

  return {
    weeklyDemand,
    loading,
    error,
    refresh
  }
}

export default useDemandPlanningData
